import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Flame, Zap, Clock, Target, ChevronDown, Clock as ClockIcon } from 'lucide-react';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import { skillPaths } from '../data/learningContent';
import { getSkillLabel } from '../data/skillAreas';

const SAMPLE_CHALLENGES = [
  {
    title: 'Make AI disagree with you',
    skill_areas: ['critique', 'reasoning'],
    duration_minutes: 7,
    description: 'Write a prompt that asks AI to argue against your position as convincingly as possible.',
  },
  {
    title: 'Spot the hallucination',
    skill_areas: ['verification', 'research'],
    duration_minutes: 5,
    description: 'Read an AI-generated paragraph and identify every claim that is fabricated or unsupported.',
  },
  {
    title: 'Rewrite a bad prompt',
    skill_areas: ['prompting', 'context'],
    duration_minutes: 6,
    description: 'Take a vague, failing prompt and rewrite it to produce a specific, useful response.',
  },
  {
    title: 'Stress-test a decision',
    skill_areas: ['reasoning', 'critique'],
    duration_minutes: 8,
    description: 'Use AI to build the strongest case against a decision you are about to make.',
  },
];

const FAQS = [
  {
    q: 'Do I need to know how to use AI already?',
    a: 'No. Challenges are designed for any level. If you are a complete beginner, you will start with simpler challenges and build up. If you already use AI regularly, you will find challenges that push your skills further. The grading adapts to where you are.',
  },
  {
    q: 'How long does each challenge take?',
    a: 'Most challenges take five to eight minutes. You read the task, write your response, submit it for grading, and read the feedback. The short format is intentional — it is long enough to complete a full rep and short enough that you will actually do it every day.',
  },
  {
    q: 'What if I am a complete beginner?',
    a: 'Start with the free challenge on the Try page. It walks you through one full challenge with no signup required. After that, you can create an account and get a new challenge every day, starting at your level and building up.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. There are no contracts or commitments. You can cancel your subscription from the settings page at any time. You keep access until the end of your current billing period.',
  },
  {
    q: 'What tools does PromptGPT work with?',
    a: 'PromptGPT teaches skills that transfer to any AI tool — ChatGPT, Claude, Gemini, or any other. The challenges are tool-agnostic. You practice the underlying skills, not a specific interface.',
  },
  {
    q: 'Is there a free option?',
    a: 'Yes. You can complete one full challenge with AI grading on the Try page without creating an account. After that, the free plan gives you a daily challenge and tracks your skill score across all 10 areas.',
  },
  {
    q: 'How does the AI grading work?',
    a: 'Each challenge has a rubric that defines what a strong response looks like. When you submit, an AI grader evaluates your work against the rubric and returns a score plus specific feedback. You see which skills improved and by how much.',
  },
  {
    q: 'What is the AI Skill Score?',
    a: 'Your AI Skill Score tracks 10 skill areas — prompting, context, research, writing, reasoning, critique, verification, automation, creativity, and tool use. Every challenge you complete moves the needle on specific areas. Your overall score is the sum of all ten.',
  },
];

export default function LandingPage() {
  useDocumentMeta({
    title: 'PromptGPT — Practice AI skills daily. Get graded. Level up.',
    description:
      'A daily AI workout. Complete challenges, get graded by AI, and watch your skill score climb. One challenge at a time, seven minutes a day.',
    canonical: 'https://promptgpt.ai/',
  });

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="bg-warm-50">
      <section className="border-b border-ink-100" aria-labelledby="hero-heading">
        <div className="container-app py-20 md:py-28 max-w-4xl">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-4 bg-white border border-ink-100 rounded-full px-3 py-1.5">
            A daily AI workout, not another course
          </div>
          <h1
            id="hero-heading"
            className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-6"
          >
            AI is a skill.
            <br />
            <span className="text-ink-500">Practice it daily.</span>
          </h1>
          <p className="text-xl text-ink-600 max-w-2xl leading-relaxed mb-8">
            One challenge. Seven minutes. Get graded by AI and watch your skill score climb.
            No courses, no modules, no curriculum — just daily practice that works.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/try" className="btn-primary">
              Try a challenge free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login?mode=signup" className="btn-secondary">
              Create account
            </Link>
          </div>
          <p className="text-sm text-ink-500 pt-3">
            Complete one full challenge without signing up. Save your score when you are ready.
          </p>
          <div className="flex flex-wrap gap-x-8 gap-y-2 pt-6 text-sm text-ink-500">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-ink-700" /> Graded by AI
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-ink-700" /> Track 10 skill areas
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-ink-700" /> Build streaks
            </div>
          </div>
        </div>
      </section>

      {/* How it works — visual flow */}
      <section className="border-b border-ink-100 bg-white" id="how-it-works" aria-labelledby="how-heading">
        <div className="container-app py-20 max-w-5xl">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
            How it works
          </div>
          <h2 id="how-heading" className="font-display text-4xl md:text-5xl mb-3">
            One challenge at a time.
          </h2>
          <p className="text-ink-600 text-lg leading-relaxed mb-10 max-w-2xl">
            Not a course catalog. Not a video library. A single daily challenge that trains a real AI skill, graded instantly.
          </p>
          <div className="relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-[3.25rem] left-[16.66%] right-[16.66%] h-px bg-ink-200" />
            <div className="grid md:grid-cols-3 gap-4 mb-2 relative">
              <div className="bg-warm-50 border border-ink-100 rounded-2xl p-6 relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-white border border-ink-100 grid place-items-center shrink-0">
                    <Target className="w-4 h-4 text-ink-700" />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-400">Step 1</span>
                </div>
                <div className="font-display text-xl mb-2">Take a challenge</div>
                <p className="text-sm text-ink-600 leading-relaxed">
                  Each challenge trains specific AI skills — prompting, reasoning, verification, and more. Seven minutes, one prompt.
                </p>
              </div>
              <div className="bg-warm-50 border border-ink-100 rounded-2xl p-6 relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-white border border-ink-100 grid place-items-center shrink-0">
                    <Zap className="w-4 h-4 text-ink-700" />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-400">Step 2</span>
                </div>
                <div className="font-display text-xl mb-2">Get graded by AI</div>
                <p className="text-sm text-ink-600 leading-relaxed">
                  Submit your work and get instant feedback with a score. See exactly which skills improved and by how much.
                </p>
              </div>
              <div className="bg-warm-50 border border-ink-100 rounded-2xl p-6 relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-white border border-ink-100 grid place-items-center shrink-0">
                    <Flame className="w-4 h-4 text-ink-700" />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-400">Step 3</span>
                </div>
                <div className="font-display text-xl mb-2">Level up</div>
                <p className="text-sm text-ink-600 leading-relaxed">
                  Your AI Skill Score tracks 10 skill areas. Build streaks, hit milestones, and watch your score climb week over week.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample challenges */}
      <section className="border-b border-ink-100" id="challenges" aria-labelledby="challenges-heading">
        <div className="container-app py-20 max-w-5xl">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
            What you can do today
          </div>
          <h2 id="challenges-heading" className="font-display text-4xl md:text-5xl mb-3">
            Real challenges. Real skills.
          </h2>
          <p className="text-ink-600 text-lg leading-relaxed mb-10 max-w-2xl">
            These are the kinds of challenges you will see inside. Each one targets specific skills and takes less than ten minutes.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {SAMPLE_CHALLENGES.map((c) => (
              <Link
                key={c.title}
                to="/try"
                className="group bg-white border border-ink-100 rounded-2xl p-6 hover:border-ink-200 hover:shadow-soft transition-all"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h3 className="font-display text-xl leading-snug text-ink-900 group-hover:text-accent-700 transition-colors">
                    {c.title}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-xs text-ink-500 shrink-0">
                    <ClockIcon className="w-3.5 h-3.5" /> {c.duration_minutes} min
                  </span>
                </div>
                <p className="text-sm text-ink-600 leading-relaxed mb-4">{c.description}</p>
                <div className="flex flex-wrap gap-2">
                  {c.skill_areas.map((area) => (
                    <span key={area} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-accent-50 text-accent-700 border border-accent-100">
                      {getSkillLabel(area)}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/try" className="btn-primary">
              Try one now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-ink-100" id="skill-score" aria-labelledby="score-heading">
        <div className="container-app py-20 max-w-4xl">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
            AI Skill Score
          </div>
          <h2 id="score-heading" className="font-display text-4xl md:text-5xl mb-3">
            Ten skills. One score.
          </h2>
          <p className="text-ink-600 text-lg leading-relaxed mb-10 max-w-2xl">
            Every challenge you complete moves the needle on specific skill areas. Your overall AI Skill Score is the sum of all ten.
          </p>

          <div className="bg-white border border-ink-100 rounded-2xl p-8 max-w-lg">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-1">AI Skill Score</div>
                <div className="font-display text-6xl font-semibold leading-none text-accent-700">642</div>
              </div>
              <div className="text-sm text-mint-600 font-medium pb-2">+12 this week</div>
            </div>
            <div className="space-y-2.5">
              {[
                { label: 'Research', score: 72 },
                { label: 'Writing', score: 81 },
                { label: 'Reasoning', score: 58 },
                { label: 'Automation', score: 34 },
                { label: 'Prompting', score: 76 },
              ].map((s, i) => (
                <div key={s.label} className="flex items-center gap-3">
                  <div className="text-xs text-ink-600 w-24 shrink-0">{s.label}</div>
                  <div className="flex-1 h-1.5 bg-ink-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-accent-500"
                      style={{ width: `${s.score}%`, transitionDelay: `${i * 50}ms` }}
                    />
                  </div>
                  <div className="text-xs text-ink-500 font-mono w-8 text-right tabular-nums">{s.score}</div>
                </div>
              ))}
            </div>
            <div className="text-xs text-ink-400 mt-4">5 of 10 skill areas shown</div>
          </div>
        </div>
      </section>

      <section className="border-b border-ink-100 bg-white" id="byop" aria-labelledby="byop-heading">
        <div className="container-app py-20 max-w-4xl">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
            Bring Your Own Problem
          </div>
          <h2 id="byop-heading" className="font-display text-4xl md:text-5xl mb-3">
            Turn a real task into a coaching session.
          </h2>
          <p className="text-ink-600 text-lg leading-relaxed mb-8 max-w-2xl">
            Tell us what you need to get done. We will guide you through it step by step, teaching AI skills along the way.
            At the end, see exactly which skills you practiced.
          </p>
          <div className="bg-warm-50 border border-ink-100 rounded-2xl p-6 max-w-lg">
            <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-2">What are you trying to get done?</div>
            <div className="bg-white border border-ink-200 rounded-xl px-4 py-3 text-ink-400 text-sm mb-3">
              I need to prepare a presentation for our Q4 strategy.
            </div>
            <div className="flex items-center gap-2 text-sm text-ink-500">
              <Clock className="w-4 h-4" /> The coach breaks it into steps and teaches skills as you go
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-ink-100" id="paths" aria-labelledby="paths-heading">
        <div className="container-app py-20 max-w-5xl">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
            Practice paths
          </div>
          <h2 id="paths-heading" className="font-display text-4xl md:text-5xl mb-3">
            Pick where you are.
          </h2>
          <p className="text-ink-600 text-lg leading-relaxed mb-10 max-w-2xl">
            Each path is a series of challenges targeting one area. Same shape, different depth. Start anywhere.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {skillPaths.slice(0, 4).map((p) => (
              <Link
                key={p.slug}
                to="/login?mode=signup"
                className="block bg-white border border-ink-100 hover:border-ink-200 hover:shadow-soft rounded-2xl p-6 transition"
              >
                <div className="font-display text-xl mb-2">{p.title}</div>
                <p className="text-sm text-ink-600 leading-relaxed">{p.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-ink-100 bg-white" id="faq" aria-labelledby="faq-heading">
        <div className="container-app py-20 max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
            Questions
          </div>
          <h2 id="faq-heading" className="font-display text-4xl md:text-5xl mb-10">
            Frequently asked.
          </h2>
          <div className="space-y-2">
            {FAQS.map((faq, i) => (
              <div key={i} className="border border-ink-100 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-warm-50 transition"
                >
                  <span className="font-display text-lg text-ink-900">{faq.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-ink-400 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-ink-600 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-warm-100" aria-labelledby="cta-heading">
        <div className="container-app py-20 text-center max-w-2xl mx-auto">
          <h2 id="cta-heading" className="font-display text-4xl md:text-5xl mb-4">
            Seven minutes. One challenge.
          </h2>
          <p className="text-ink-600 text-lg mb-8 leading-relaxed">
            Practice AI as a skill, not a subject.<br />
            For anyone who would rather train than watch.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/try" className="btn-primary">
              Try a challenge free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/pricing" className="btn-secondary">
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
